import { Button, Grid, TextField } from '@mui/material';
import { useState } from 'react';
import { useAuth, useProvideSnackBar, useSendNotification } from '../../hooks';
import { StyledCard } from '../Layout/styles';
import { Send } from '@mui/icons-material';
import { PartnerWorkerDeviceDetails, WorkerDevice, WorkerDeviceDetailsByTravel } from '../../@types/employeeData';
import { TAB_VIEW } from '../../@types';
import { GridRowId } from '@mui/x-data-grid-pro';
import { getLineDataGridId } from './LineDataGrid';
import { getWorkerDeviceDataGridId } from './WorkerDeviceDataGrid';

type SendNotificationProps = {
    workerTravelDevices: WorkerDeviceDetailsByTravel[] | null;
    devicesByCompany: Record<string, PartnerWorkerDeviceDetails[]> | null;
    devicesByWorkerLanguageKey: Map<string, WorkerDevice[]> | null;
    viewTab: TAB_VIEW;
    rowSelectionModel: GridRowId[];
    onSendSuccess: () => void;
};

export default function SendNotification({
                                             workerTravelDevices,
                                             devicesByCompany,
                                             devicesByWorkerLanguageKey,
                                             viewTab,
                                             rowSelectionModel,
                                             onSendSuccess
                                         }: SendNotificationProps) {
    const { user } = useAuth();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const { showError } = useProvideSnackBar();
    const { sendNotification, isLoading } = useSendNotification({
        onSuccess: () => {
            setTitle('');
            setBody('');
            onSendSuccess();
        },
    });

    const getSelectedDevices = (): WorkerDevice[] => {
        const devices: WorkerDevice[] = [];

        if (viewTab === TAB_VIEW.COMPANY_DEVICE) {
            if (!devicesByWorkerLanguageKey) {
                return devices;
            }
            for (const rowID of rowSelectionModel) {
                const devicesForWorker = devicesByWorkerLanguageKey.get(rowID.toString());

                if (devicesForWorker) {
                    devices.push(...devicesForWorker)
                }
            }
            return devices;
        }

        if (!workerTravelDevices || !devicesByCompany) {
            showError('Hiányzó dolgozói eszközök');
            return [];
        }

        switch (viewTab) {
            case TAB_VIEW.DEVICE:
                return workerTravelDevices
                    .filter(device =>
                        rowSelectionModel.includes(getWorkerDeviceDataGridId(device)));

            case TAB_VIEW.LINE:
                return workerTravelDevices.filter(device =>
                    rowSelectionModel.includes(getLineDataGridId(device)));

            case TAB_VIEW.WORKPLACE:
                for (const rowID of rowSelectionModel) {
                    const companyDevices = devicesByCompany[rowID.toString().split('_')[0]];

                    if (companyDevices) {
                        devices.push(...companyDevices)
                    }
                }
                return devices;
            default:
                return [];
        }
    }

    const validateNotificationData = (workerDevices: WorkerDevice[]) => {
        if (title === '') {
            showError('Hiányzó cím');
            return false;
        }
        if (body === '') {
            showError('Hiányzó üzenet');
            return false;
        }
        if (workerDevices.length === 0) {
            showError('Hiányzó címzett');
            return false;
        }
        return true;
    };

    const fetchSendNotification = async () => {
        if (!user) {
            return;
        }

        const selectedDevices = getSelectedDevices()

        if (!validateNotificationData(selectedDevices)) {
            return;
        }

        await sendNotification(selectedDevices, title, body);
    };

    return (
        <StyledCard>
            <Grid container spacing={1} p={2}>
                <Grid item xs={12} py={2}>
                    <TextField
                        id='title'
                        label='Cím'
                        variant='outlined'
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} py={2}>
                    <TextField
                        id='body'
                        label='Üzenet'
                        variant='outlined'
                        value={body}
                        onChange={e => setBody(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        multiline
                        rows={2}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button variant='contained' color='primary' onClick={fetchSendNotification} fullWidth
                            endIcon={<Send />} disabled={isLoading}>
                        {isLoading ? 'Küldés folyamatban...' : 'Küldés'}
                    </Button>
                </Grid>
            </Grid>
        </StyledCard>
    );
}
